// Workflow API Service
// Manages API connections and authentication for workflow nodes

class WorkflowAPIService {
  constructor() {
    this.apiConfigs = new Map();
    this.activeConnections = new Map();
    this.connectionPool = new Map();
    this.rateLimiters = new Map();
    this.requestQueues = new Map();
    this.tokenRefreshPromises = new Map();
    this.initializeAPIConfigs();
    this.setupRateLimiting();
  }

  initializeAPIConfigs() {
    // Google Services
    this.apiConfigs.set('google', {
      authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
      tokenUrl: 'https://oauth2.googleapis.com/token',
      scopes: {
        gmail: 'https://www.googleapis.com/auth/gmail.modify',
        sheets: 'https://www.googleapis.com/auth/spreadsheets',
        drive: 'https://www.googleapis.com/auth/drive.file',
        calendar: 'https://www.googleapis.com/auth/calendar',
        analytics: 'https://www.googleapis.com/auth/analytics.readonly'
      },
      clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      clientSecret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET
    });

    // Slack
    this.apiConfigs.set('slack', {
      authUrl: 'https://slack.com/oauth/v2/authorize',
      tokenUrl: 'https://slack.com/api/oauth.v2.access',
      scopes: ['chat:write', 'channels:read', 'users:read', 'files:write'],
      clientId: process.env.REACT_APP_SLACK_CLIENT_ID,
      clientSecret: process.env.REACT_APP_SLACK_CLIENT_SECRET
    });

    // Instagram/Facebook
    this.apiConfigs.set('facebook', {
      authUrl: 'https://www.facebook.com/v18.0/dialog/oauth',
      tokenUrl: 'https://graph.facebook.com/v18.0/oauth/access_token',
      scopes: [
        'pages_manage_posts',
        'pages_read_engagement',
        'instagram_basic',
        'instagram_content_publish'
      ],
      clientId: process.env.REACT_APP_FACEBOOK_APP_ID,
      clientSecret: process.env.REACT_APP_FACEBOOK_APP_SECRET
    });

    // OpenAI
    this.apiConfigs.set('openai', {
      apiKey: process.env.REACT_APP_OPENAI_API_KEY,
      baseUrl: 'https://api.openai.com/v1',
      models: ['gpt-4', 'gpt-3.5-turbo', 'dall-e-3', 'whisper-1']
    });

    // Shopify
    this.apiConfigs.set('shopify', {
      authUrl: 'https://{shop}.myshopify.com/admin/oauth/authorize',
      tokenUrl: 'https://{shop}.myshopify.com/admin/oauth/access_token',
      scopes: ['read_products', 'write_products', 'read_orders', 'write_orders'],
      apiKey: process.env.REACT_APP_SHOPIFY_API_KEY,
      apiSecret: process.env.REACT_APP_SHOPIFY_API_SECRET
    });

    // Stripe
    this.apiConfigs.set('stripe', {
      apiKey: process.env.REACT_APP_STRIPE_SECRET_KEY,
      publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
      baseUrl: 'https://api.stripe.com/v1'
    });

    // Airtable
    this.apiConfigs.set('airtable', {
      apiKey: process.env.REACT_APP_AIRTABLE_API_KEY,
      baseUrl: 'https://api.airtable.com/v0'
    });

    // Discord
    this.apiConfigs.set('discord', {
      authUrl: 'https://discord.com/api/oauth2/authorize',
      tokenUrl: 'https://discord.com/api/oauth2/token',
      scopes: ['bot', 'applications.commands'],
      clientId: process.env.REACT_APP_DISCORD_CLIENT_ID,
      clientSecret: process.env.REACT_APP_DISCORD_CLIENT_SECRET
    });
  }

  // Setup rate limiting for different APIs
  setupRateLimiting() {
    // Define rate limits per service (requests per minute)
    const rateLimits = {
      google: { requests: 1000, window: 60000 },
      slack: { requests: 100, window: 60000 },
      facebook: { requests: 600, window: 3600000 }, // Per hour
      openai: { requests: 60, window: 60000 },
      shopify: { requests: 40, window: 1000 },
      stripe: { requests: 1000, window: 1000 },
      twitter: { requests: 300, window: 900000 }, // Per 15 minutes
      linkedin: { requests: 100, window: 60000 }
    };

    for (const [service, limit] of Object.entries(rateLimits)) {
      this.rateLimiters.set(service, {
        requests: [],
        limit: limit.requests,
        window: limit.window
      });
      this.requestQueues.set(service, []);
    }
  }

  // Check and enforce rate limits
  async checkRateLimit(service) {
    const rateLimiter = this.rateLimiters.get(service);
    if (!rateLimiter) return true;

    const now = Date.now();
    
    // Clean old requests
    rateLimiter.requests = rateLimiter.requests.filter(
      time => now - time < rateLimiter.window
    );

    // Check if under limit
    if (rateLimiter.requests.length < rateLimiter.limit) {
      rateLimiter.requests.push(now);
      return true;
    }

    // Calculate wait time
    const oldestRequest = rateLimiter.requests[0];
    const waitTime = rateLimiter.window - (now - oldestRequest);
    
    if (waitTime > 0) {
      console.log(`Rate limit hit for ${service}, waiting ${waitTime}ms`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
      return this.checkRateLimit(service);
    }

    return true;
  }

  // Queue-based request processing
  async queueRequest(service, requestFn) {
    return new Promise((resolve, reject) => {
      const queue = this.requestQueues.get(service) || [];
      queue.push({ requestFn, resolve, reject });
      this.requestQueues.set(service, queue);
      this.processQueue(service);
    });
  }

  // Process queued requests
  async processQueue(service) {
    const queue = this.requestQueues.get(service);
    if (!queue || queue.length === 0) return;

    const { requestFn, resolve, reject } = queue.shift();
    
    try {
      await this.checkRateLimit(service);
      const result = await requestFn();
      resolve(result);
    } catch (error) {
      reject(error);
    }

    // Process next request
    if (queue.length > 0) {
      setImmediate(() => this.processQueue(service));
    }
  }

  // === OAuth Flow Methods ===
  async initiateOAuth(service, redirectUri) {
    const config = this.apiConfigs.get(service);
    if (!config) {
      throw new Error(`Service ${service} not configured`);
    }

    const params = new URLSearchParams({
      client_id: config.clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: Array.isArray(config.scopes) 
        ? config.scopes.join(' ') 
        : Object.values(config.scopes).join(' '),
      access_type: 'offline', // For refresh tokens
      prompt: 'consent'
    });

    return `${config.authUrl}?${params.toString()}`;
  }

  async exchangeCodeForToken(service, code, redirectUri) {
    const config = this.apiConfigs.get(service);
    if (!config) {
      throw new Error(`Service ${service} not configured`);
    }

    const response = await fetch(config.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        client_id: config.clientId,
        client_secret: config.clientSecret,
        code,
        redirect_uri: redirectUri,
        grant_type: 'authorization_code'
      })
    });

    const data = await response.json();
    
    // Store tokens securely
    this.storeTokens(service, data);
    
    return data;
  }

  async refreshToken(service, refreshToken) {
    // Prevent multiple concurrent refresh attempts
    const refreshKey = `${service}_${refreshToken}`;
    if (this.tokenRefreshPromises.has(refreshKey)) {
      return this.tokenRefreshPromises.get(refreshKey);
    }

    const refreshPromise = this._performTokenRefresh(service, refreshToken);
    this.tokenRefreshPromises.set(refreshKey, refreshPromise);

    try {
      const result = await refreshPromise;
      this.tokenRefreshPromises.delete(refreshKey);
      return result;
    } catch (error) {
      this.tokenRefreshPromises.delete(refreshKey);
      throw error;
    }
  }

  async _performTokenRefresh(service, refreshToken) {
    const config = this.apiConfigs.get(service);
    if (!config) {
      throw new Error(`Service ${service} not configured`);
    }

    console.log(`Refreshing token for ${service}`);

    try {
      const response = await fetch(config.tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          client_id: config.clientId,
          client_secret: config.clientSecret,
          refresh_token: refreshToken,
          grant_type: 'refresh_token'
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Token refresh failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      // Update stored tokens
      this.storeTokens(service, {
        ...data,
        refresh_token: data.refresh_token || refreshToken // Keep old refresh token if new one not provided
      });
      
      console.log(`Token refreshed successfully for ${service}`);
      return data;
    } catch (error) {
      console.error(`Token refresh failed for ${service}:`, error);
      throw error;
    }
  }

  // === API Client Methods ===
  async getAPIClient(service, userTokens) {
    const config = this.apiConfigs.get(service);
    if (!config) {
      throw new Error(`Service ${service} not configured`);
    }

    // Check if token needs refresh
    if (userTokens?.expiresAt && Date.now() >= userTokens.expiresAt) {
      const refreshedTokens = await this.refreshToken(service, userTokens.refreshToken);
      userTokens = refreshedTokens;
    }

    switch (service) {
      case 'google':
        return this.createGoogleClient(userTokens);
      case 'slack':
        return this.createSlackClient(userTokens);
      case 'shopify':
        return this.createShopifyClient(userTokens);
      case 'stripe':
        return this.createStripeClient();
      case 'openai':
        return this.createOpenAIClient();
      case 'airtable':
        return this.createAirtableClient();
      default:
        return this.createGenericClient(service, userTokens);
    }
  }

  createGoogleClient(tokens) {
    return {
      gmail: {
        send: async (params) => {
          return await this.makeRequest(
            'https://gmail.googleapis.com/gmail/v1/users/me/messages/send',
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${tokens.accessToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(params)
            }
          );
        },
        list: async (params) => {
          const queryString = new URLSearchParams(params).toString();
          return await this.makeRequest(
            `https://gmail.googleapis.com/gmail/v1/users/me/messages?${queryString}`,
            {
              headers: {
                'Authorization': `Bearer ${tokens.accessToken}`
              }
            }
          );
        }
      },
      sheets: {
        append: async (spreadsheetId, range, values) => {
          return await this.makeRequest(
            `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}:append?valueInputOption=USER_ENTERED`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${tokens.accessToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ values })
            }
          );
        },
        get: async (spreadsheetId, range) => {
          return await this.makeRequest(
            `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
            {
              headers: {
                'Authorization': `Bearer ${tokens.accessToken}`
              }
            }
          );
        }
      },
      drive: {
        upload: async (file, metadata) => {
          const boundary = '-------314159265358979323846';
          const delimiter = "\r\n--" + boundary + "\r\n";
          const closeDelim = "\r\n--" + boundary + "--";

          const requestBody = 
            delimiter +
            'Content-Type: application/json\r\n\r\n' +
            JSON.stringify(metadata) +
            delimiter +
            'Content-Type: ' + file.type + '\r\n\r\n' +
            file +
            closeDelim;

          return await this.makeRequest(
            'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${tokens.accessToken}`,
                'Content-Type': `multipart/related; boundary="${boundary}"`
              },
              body: requestBody
            }
          );
        }
      }
    };
  }

  createSlackClient(tokens) {
    return {
      chat: {
        postMessage: async (params) => {
          return await this.makeRequest(
            'https://slack.com/api/chat.postMessage',
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${tokens.accessToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(params)
            }
          );
        }
      },
      channels: {
        list: async () => {
          return await this.makeRequest(
            'https://slack.com/api/conversations.list',
            {
              headers: {
                'Authorization': `Bearer ${tokens.accessToken}`
              }
            }
          );
        }
      }
    };
  }

  createShopifyClient(tokens) {
    const shop = tokens.shop;
    const baseUrl = `https://${shop}.myshopify.com/admin/api/2024-01`;
    
    return {
      orders: {
        create: async (orderData) => {
          return await this.makeRequest(
            `${baseUrl}/orders.json`,
            {
              method: 'POST',
              headers: {
                'X-Shopify-Access-Token': tokens.accessToken,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ order: orderData })
            }
          );
        },
        list: async (params = {}) => {
          const queryString = new URLSearchParams(params).toString();
          return await this.makeRequest(
            `${baseUrl}/orders.json?${queryString}`,
            {
              headers: {
                'X-Shopify-Access-Token': tokens.accessToken
              }
            }
          );
        }
      },
      products: {
        create: async (productData) => {
          return await this.makeRequest(
            `${baseUrl}/products.json`,
            {
              method: 'POST',
              headers: {
                'X-Shopify-Access-Token': tokens.accessToken,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ product: productData })
            }
          );
        },
        update: async (productId, productData) => {
          return await this.makeRequest(
            `${baseUrl}/products/${productId}.json`,
            {
              method: 'PUT',
              headers: {
                'X-Shopify-Access-Token': tokens.accessToken,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ product: productData })
            }
          );
        }
      }
    };
  }

  createStripeClient() {
    const stripe = {
      baseUrl: 'https://api.stripe.com/v1',
      apiKey: this.apiConfigs.get('stripe').apiKey,
      
      payments: {
        create: async (params) => {
          return await this.makeRequest(
            `${stripe.baseUrl}/payment_intents`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${stripe.apiKey}`,
                'Content-Type': 'application/x-www-form-urlencoded'
              },
              body: new URLSearchParams(params).toString()
            }
          );
        }
      },
      customers: {
        create: async (params) => {
          return await this.makeRequest(
            `${stripe.baseUrl}/customers`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${stripe.apiKey}`,
                'Content-Type': 'application/x-www-form-urlencoded'
              },
              body: new URLSearchParams(params).toString()
            }
          );
        }
      }
    };
    
    return stripe;
  }

  createOpenAIClient() {
    const apiKey = this.apiConfigs.get('openai').apiKey;
    const baseUrl = this.apiConfigs.get('openai').baseUrl;
    
    return {
      chat: {
        completions: async (params) => {
          return await this.makeRequest(
            `${baseUrl}/chat/completions`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${apiKey}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(params)
            }
          );
        }
      },
      images: {
        generate: async (params) => {
          return await this.makeRequest(
            `${baseUrl}/images/generations`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${apiKey}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(params)
            }
          );
        }
      },
      audio: {
        transcriptions: async (formData) => {
          return await this.makeRequest(
            `${baseUrl}/audio/transcriptions`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${apiKey}`
              },
              body: formData
            }
          );
        }
      }
    };
  }

  createAirtableClient() {
    const apiKey = this.apiConfigs.get('airtable').apiKey;
    const baseUrl = this.apiConfigs.get('airtable').baseUrl;
    
    return {
      base: (baseId) => ({
        table: (tableName) => ({
          create: async (records) => {
            return await this.makeRequest(
              `${baseUrl}/${baseId}/${tableName}`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${apiKey}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ records })
              }
            );
          },
          list: async (params = {}) => {
            const queryString = new URLSearchParams(params).toString();
            return await this.makeRequest(
              `${baseUrl}/${baseId}/${tableName}?${queryString}`,
              {
                headers: {
                  'Authorization': `Bearer ${apiKey}`
                }
              }
            );
          }
        })
      })
    };
  }

  createGenericClient(service, tokens) {
    return {
      request: async (url, options = {}) => {
        const headers = {
          ...options.headers,
          'Authorization': `Bearer ${tokens.accessToken}`
        };
        
        return await this.makeRequest(url, { ...options, headers });
      }
    };
  }

  // === Helper Methods ===
  async makeRequest(url, options = {}, retryCount = 0) {
    const maxRetries = options.maxRetries || 3;
    const service = this.extractServiceFromUrl(url);
    
    try {
      // Apply rate limiting
      if (service) {
        await this.checkRateLimit(service);
      }

      const response = await fetch(url, {
        ...options,
        timeout: options.timeout || 30000
      });
      
      // Handle specific HTTP status codes
      if (response.status === 429) {
        // Rate limited - check for Retry-After header
        const retryAfter = response.headers.get('retry-after');
        const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : 5000;
        
        if (retryCount < maxRetries) {
          console.log(`Rate limited, waiting ${waitTime}ms before retry ${retryCount + 1}/${maxRetries}`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return this.makeRequest(url, options, retryCount + 1);
        }
      }
      
      if (response.status === 401) {
        // Unauthorized - token might be expired
        throw new Error('Authentication failed - token may be expired');
      }
      
      if ([500, 502, 503, 504].includes(response.status)) {
        // Server errors - retry with exponential backoff
        if (retryCount < maxRetries) {
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 10000);
          console.log(`Server error ${response.status}, retrying in ${waitTime}ms (${retryCount + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return this.makeRequest(url, options, retryCount + 1);
        }
      }
      
      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error?.message || errorData.message || response.statusText;
        } catch {
          errorMessage = await response.text() || response.statusText;
        }
        throw new Error(`API request failed: ${response.status} - ${errorMessage}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return await response.json();
      }
      
      return await response.text();
    } catch (error) {
      // Network errors - retry if not at max retries
      if (retryCount < maxRetries && this.isRetryableError(error)) {
        const waitTime = Math.min(1000 * Math.pow(2, retryCount), 10000);
        console.log(`Network error, retrying in ${waitTime}ms (${retryCount + 1}/${maxRetries}): ${error.message}`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        return this.makeRequest(url, options, retryCount + 1);
      }
      
      console.error('API request error:', error);
      throw error;
    }
  }

  // Extract service name from URL for rate limiting
  extractServiceFromUrl(url) {
    if (url.includes('googleapis.com')) return 'google';
    if (url.includes('slack.com')) return 'slack';
    if (url.includes('graph.facebook.com')) return 'facebook';
    if (url.includes('api.openai.com')) return 'openai';
    if (url.includes('myshopify.com')) return 'shopify';
    if (url.includes('api.stripe.com')) return 'stripe';
    if (url.includes('api.twitter.com')) return 'twitter';
    if (url.includes('api.linkedin.com')) return 'linkedin';
    if (url.includes('api.airtable.com')) return 'airtable';
    if (url.includes('api.notion.com')) return 'notion';
    if (url.includes('discord.com')) return 'discord';
    return null;
  }

  // Check if error is retryable
  isRetryableError(error) {
    const retryablePatterns = [
      /network/i,
      /timeout/i,
      /fetch/i,
      /connection/i,
      /ECONNRESET/i,
      /ENOTFOUND/i,
      /ETIMEDOUT/i
    ];
    
    return retryablePatterns.some(pattern => pattern.test(error.message));
  }

  // Enhanced connection health monitoring
  async checkConnectionHealth(service) {
    const tokens = this.getStoredTokens(service);
    if (!tokens) {
      return { healthy: false, reason: 'No tokens stored' };
    }

    try {
      const client = await this.getAPIClient(service, tokens);
      
      // Service-specific health checks
      switch (service) {
        case 'google':
          await client.gmail.list({ maxResults: 1 });
          break;
        case 'slack':
          await client.channels.list();
          break;
        // Add more service-specific health checks
        default:
          return { healthy: true, reason: 'No specific health check available' };
      }

      return { healthy: true, lastChecked: new Date() };
    } catch (error) {
      return { 
        healthy: false, 
        reason: error.message,
        lastChecked: new Date()
      };
    }
  }

  storeTokens(service, tokens) {
    const storedTokens = {
      ...tokens,
      service,
      storedAt: Date.now(),
      expiresAt: tokens.expires_in 
        ? Date.now() + (tokens.expires_in * 1000)
        : null
    };
    
    // Store in localStorage (in production, use secure storage)
    localStorage.setItem(`workflow_tokens_${service}`, JSON.stringify(storedTokens));
    
    // Also keep in memory
    this.activeConnections.set(service, storedTokens);
  }

  getStoredTokens(service) {
    // First check memory
    const memoryTokens = this.activeConnections.get(service);
    if (memoryTokens) return memoryTokens;
    
    // Then check localStorage
    const storedTokens = localStorage.getItem(`workflow_tokens_${service}`);
    if (storedTokens) {
      const tokens = JSON.parse(storedTokens);
      this.activeConnections.set(service, tokens);
      return tokens;
    }
    
    return null;
  }

  clearTokens(service) {
    this.activeConnections.delete(service);
    localStorage.removeItem(`workflow_tokens_${service}`);
  }

  isServiceConnected(service) {
    const tokens = this.getStoredTokens(service);
    if (!tokens) return false;
    
    // Check if token is expired
    if (tokens.expiresAt && Date.now() >= tokens.expiresAt) {
      // Try to refresh if we have a refresh token
      if (tokens.refreshToken) {
        return true; // Will refresh on next use
      }
      return false;
    }
    
    return true;
  }

  getConnectedServices() {
    const services = [];
    
    for (const [service] of this.apiConfigs) {
      if (this.isServiceConnected(service)) {
        services.push({
          name: service,
          connected: true,
          tokens: this.getStoredTokens(service)
        });
      }
    }
    
    return services;
  }
}

const workflowAPIService = new WorkflowAPIService();
export default workflowAPIService;